import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { Megaphone, Pin, Plus, X, Send, AlertTriangle, Info, Bell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const severityColors: Record<string, string> = {
  urgent: "border-destructive/30 bg-destructive/5",
  important: "border-amber-300 bg-amber-50",
  normal: "border-border bg-card",
};

const severityIcons: Record<string, typeof Info> = { urgent: AlertTriangle, important: Bell, normal: Info };

const EmployeeAnnouncements = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [priority, setPriority] = useState("normal");

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => {
      const { data, error } = await supabase.from("announcements").select("*").order("is_pinned", { ascending: false }).order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("announcements").insert({ title, content, priority, author_id: user?.id });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      setShowForm(false); setTitle(""); setContent(""); setPriority("normal");
      toast({ title: "Announcement posted" });
    },
  });

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Announcements</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Company news & updates</p>
        </div>
        <Button size="sm" variant={showForm ? "outline" : "default"} className={showForm ? "" : "bg-teal hover:bg-teal-light text-white"} onClick={() => setShowForm(!showForm)}>
          {showForm ? <><X className="w-4 h-4 mr-1" /> Cancel</> : <><Plus className="w-4 h-4 mr-1" /> New Post</>}
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Announcement title..." />
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Write your announcement..." rows={4} />
            <div className="flex items-center gap-3">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => createMutation.mutate()} disabled={!title || !content} className="bg-teal hover:bg-teal-light text-white gap-2">
                <Send className="w-3.5 h-3.5" /> Post
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
      ) : announcements.length === 0 ? (
        <Card><CardContent className="p-16 text-center"><Megaphone className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" /><p className="text-muted-foreground font-body">No announcements yet</p></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((a) => {
            const Icon = severityIcons[a.priority] || Info;
            return (
              <Card key={a.id} className={`${severityColors[a.priority] || severityColors.normal}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${a.priority === "urgent" ? "bg-destructive/10" : a.priority === "important" ? "bg-amber-100" : "bg-teal/10"}`}>
                      <Icon className={`w-5 h-5 ${a.priority === "urgent" ? "text-destructive" : a.priority === "important" ? "text-amber-600" : "text-teal"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {a.is_pinned && <Pin className="w-3 h-3 text-teal" />}
                        <h3 className="font-display text-lg text-foreground">{a.title}</h3>
                      </div>
                      <p className="text-muted-foreground font-body text-sm leading-relaxed whitespace-pre-wrap">{a.content}</p>
                      <p className="text-muted-foreground/50 font-body text-xs mt-3">{new Date(a.created_at).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </IntranetLayout>
  );
};

export default EmployeeAnnouncements;
